import Cookies from 'js-cookie';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { decryptData } from 'src/lib/crypto';

export interface AppItem {
    nama: string;
    domain: string;
    singkatan: string;
}

export interface UserData {
    id?: string;
    name?: string;
    email?: string;
    group?: string;
    mac_address?: string;
    app?: AppItem[];
    [key: string]: any;
}

interface UserDataContextType {
    userData: UserData | null;
    userApps: AppItem[];
    setUserData: (data: UserData | null) => void;
    isLoading: boolean;
    fetchUserData: () => Promise<void>;
}

const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

interface UserDataProviderProps {
    children: ReactNode;
}

export const UserDataProvider: React.FC<UserDataProviderProps> = ({ children }) => {
    const [userData, setUserData] = useState<UserData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const sessionId = Cookies.get("osm_sess");
    const API_KEY = process.env.NEXT_PUBLIC_API_KEY as string;

    const fetchUserData = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/session/get', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(sessionId),
            });

            if (response.ok) {
                const encryptedData = await response.json();
                const decryptedUserData = decryptData(encryptedData);
                const userData = decryptedUserData[API_KEY];
                setUserData(userData);
            } else {
                console.error('Failed to fetch user data');
                setUserData(null);
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            setUserData(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Fetch user data saat component mount
        fetchUserData();
    }, []);

    const userApps = userData?.app || [];

    const contextValue: UserDataContextType = {
        userData,
        userApps,
        setUserData,
        isLoading,
        fetchUserData,
    };

    return (
        <UserDataContext.Provider value={contextValue}>
            {children}
        </UserDataContext.Provider>
    );
};

export const useUserData = (): UserDataContextType => {
    const context = useContext(UserDataContext);
    if (context === undefined) {
        throw new Error('useUserData must be used within a UserDataProvider');
    }
    return context;
};

export default UserDataContext;